/**
 * PaymentAndRefundCard — Unified "Payment & Refund" panel for B2C / Agent /
 * Admin booking-detail surfaces. Renders:
 *   • Payment History row (mode, transaction id, paid amount, paid at)
 *   • Cancellation breakdown rows (gross paid, supplier fee, service fee
 *     retained, refund amount, refund method, refund status badge)
 *
 * Source of truth: `booking.cancellation_details` (stamped by
 * `services/cancellation_calculator.py::compute_cancellation_breakdown`).
 * Auto-hides the cancellation half until the booking actually has one.
 */
import React from 'react';
import { CreditCard, AlertCircle, Check, Clock, IndianRupee } from 'lucide-react';

const REFUND_STATUS_TONES = {
  completed: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: Check, label: 'Refund Completed' },
  pending_gateway: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: Clock, label: 'Refund Pending (5-7 days)' },
  manual_review: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: AlertCircle, label: 'Under Manual Review' },
  no_refund: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: AlertCircle, label: 'No Refund Applicable' },
};

const _money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function _formatPaymentMode(mode) {
  const m = String(mode || '').toLowerCase();
  if (m === 'wallet') return 'Agent Wallet';
  if (m === 'credit') return 'Credit Line';
  if (m === 'gateway' || m === 'razorpay' || m === 'ccavenue') return 'Online Gateway';
  if (m === 'upi') return 'UPI';
  if (m === 'card') return 'Card';
  return mode || '—';
}

export default function PaymentAndRefundCard({ booking, testIdPrefix = 'paref' }) {
  if (!booking) return null;
  const cd = booking.cancellation_details || null;
  const paymentMode = booking.payment_mode || booking.payment_method || '';
  const paymentTxn = booking.payment_transaction_id || booking.transaction_id || '';
  const paymentStatus = booking.payment_status || (booking.status === 'CONFIRMED' ? 'PAID' : '');
  const totalAmount = Number(booking.total_amount || 0);

  const showCancellation = !!cd;
  const tone = showCancellation ? REFUND_STATUS_TONES[cd.refund_status] || REFUND_STATUS_TONES.no_refund : null;
  const ToneIcon = tone?.icon || AlertCircle;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-testid={`${testIdPrefix}-card`}>
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
        <CreditCard className="w-5 h-5 text-[#335E77]" />
        Payment &amp; Refund
      </h2>

      {/* Payment block */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4" data-testid={`${testIdPrefix}-payment-block`}>
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
          <p className="text-xs text-gray-500 mb-0.5">Payment Method</p>
          <p className="font-semibold text-gray-900">{_formatPaymentMode(paymentMode)}</p>
        </div>
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
          <p className="text-xs text-gray-500 mb-0.5">Total Paid</p>
          <p className="font-semibold text-gray-900">{_money(totalAmount)}</p>
        </div>
        {paymentTxn ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 sm:col-span-2">
            <p className="text-xs text-gray-500 mb-0.5">Transaction ID</p>
            <p className="font-mono text-xs text-gray-800 break-all">{paymentTxn}</p>
          </div>
        ) : null}
        {paymentStatus ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 sm:col-span-2 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-600" />
            <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">{paymentStatus}</p>
          </div>
        ) : null}
      </div>

      {/* Cancellation block */}
      {showCancellation ? (
        <div data-testid={`${testIdPrefix}-cancellation-block`}>
          <h3 className="text-sm font-semibold text-gray-900 mb-3 mt-2 flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-[#335E77]" />
            Refund Breakdown
          </h3>
          <div className="rounded-lg border border-gray-100 divide-y divide-gray-100">
            <div className="flex justify-between items-center px-3 py-2 text-sm" data-testid={`${testIdPrefix}-row-gross`}>
              <span className="text-gray-600">Total Paid</span>
              <span className="font-semibold text-gray-900">{_money(cd.gross_paid)}</span>
            </div>
            <div className="flex justify-between items-center px-3 py-2 text-sm" data-testid={`${testIdPrefix}-row-supplier`}>
              <span className="text-gray-600">Supplier Cancellation Fee</span>
              <span className="font-medium text-rose-700">− {_money(cd.supplier_fee)}</span>
            </div>
            <div className="flex justify-between items-center px-3 py-2 text-sm" data-testid={`${testIdPrefix}-row-service-fee`}>
              <span className="text-gray-600">
                Service Fee
                <span className="ml-1.5 text-[10px] text-gray-500 italic">(non-refundable)</span>
              </span>
              <span className="font-medium text-rose-700">− {_money(cd.service_fee_retained)}</span>
            </div>
            <div className={`flex justify-between items-center px-3 py-2.5 text-base font-bold ${tone.bg}`} data-testid={`${testIdPrefix}-row-refund-amount`}>
              <span className={tone.text}>Refund Amount</span>
              <span className={tone.text}>{_money(cd.refund_amount)}</span>
            </div>
          </div>

          {/* Status banner */}
          <div className={`mt-3 rounded-lg border ${tone.border} ${tone.bg} px-3 py-2 flex items-start gap-2`} data-testid={`${testIdPrefix}-status-banner`}>
            <ToneIcon className={`w-4 h-4 ${tone.text} flex-shrink-0 mt-0.5`} />
            <div className="text-xs leading-snug">
              <p className={`font-semibold ${tone.text}`}>{tone.label}</p>
              {cd.refund_message ? (
                <p className={`${tone.text} mt-0.5`}>{cd.refund_message}</p>
              ) : null}
              {cd.refund_transaction_id ? (
                <p className={`${tone.text} mt-1 font-mono text-[10px] break-all`}>
                  Refund Txn: {cd.refund_transaction_id}
                </p>
              ) : null}
            </div>
          </div>

          {/* Meta */}
          {(cd.reason || cd.cancelled_by || cd.cancelled_at) ? (
            <div className="mt-2 text-[11px] text-gray-500 space-y-0.5" data-testid={`${testIdPrefix}-meta`}>
              {cd.reason ? <p><span className="font-semibold">Reason:</span> {cd.reason}</p> : null}
              {cd.cancelled_by ? <p><span className="font-semibold">Cancelled by:</span> {cd.cancelled_by}</p> : null}
              {cd.cancelled_at ? <p><span className="font-semibold">Cancelled at:</span> {new Date(cd.cancelled_at).toLocaleString('en-IN')}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
